/**
 * Todos os textos de tela, em português do Brasil, num só lugar (permite inglês depois).
 * Mensagens de erro de negócio vêm prontas da API; aqui ficam só os textos da própria interface.
 */
export const ptBR = {
  app: {
    module: 'PCP',
    title: 'Planejamento de Produção',
    site: 'ACG Cotia',
    logoAlt: 'ACG Packaging Materials',
    skipToContent: 'Ir para o conteúdo',
  },
  nav: {
    label: 'Seções do módulo',
    openMenu: 'Abrir menu',
    closeMenu: 'Fechar menu',
    load: 'Carga × Capacidade',
    schedule: 'Programação diária',
    orders: 'Carteira e prioridade',
    materials: 'Matéria-prima',
    master: 'Cadastros',
    import: 'Importar Excel',
    shipments: 'Importação em Andamento',
    audit: 'Auditoria',
    plans: 'Planos',
  },
  auth: {
    heading: 'PCP',
    badge: 'ACG Cotia',
    tagline: 'Planejamento e Controle de Produção',
    restricted: 'Acesso restrito — informe seu e-mail e sua senha',
    footer: 'ACG Packaging Materials',
    emailPlaceholder: 'E-mail',
    passwordPlaceholder: 'Senha',
    title: 'Entrar',
    subtitle: 'Use seu e-mail e senha do PCP.',
    email: 'E-mail',
    password: 'Senha',
    submit: 'Entrar',
    submitting: 'Entrando…',
    logout: 'Sair',
    sessionExpired: 'Sua sessão expirou. Entre novamente.',
    twoFactor: {
      title: 'Verificação em duas etapas',
      subtitle: 'Digite o código de 6 dígitos do seu aplicativo autenticador.',
      code: 'Código',
      recoverySubtitle: 'Digite um dos seus códigos de recuperação.',
      recoveryCode: 'Código de recuperação',
      useRecovery: 'Usar um código de recuperação',
      useApp: 'Usar o aplicativo autenticador',
      trust: 'Confiar neste dispositivo por 30 dias',
      trustHint:
        'Use só em computador ou tablet seu. Neste navegador, o código não será pedido de novo por 30 dias.',
      verify: 'Verificar',
      verifying: 'Verificando…',
      back: 'Voltar',
    },
    demoHint:
      'Ambiente de demonstração (dados simulados). Usuários de teste: planejador@acg.local, gestao@acg.local, operador.cal5@acg.local, ti@acg.local.',
  },
  importPage: {
    hint: 'Baixe o modelo, preencha no Excel e envie o arquivo (.xlsx ou .csv separado por ;). As linhas vão para validação; só as validadas são aplicadas.',
    type: 'Tipo',
    types: { capacity: 'Capacidade', calendar: 'Calendário', schedule: 'Programação' },
    downloadTemplate: 'Baixar modelo (.xlsx)',
    file: 'Arquivo',
    fileHint: 'A primeira linha deve ser o cabeçalho do modelo.',
    paste: 'Ou cole o conteúdo (CSV com ;)',
    pastePlaceholder: 'maquina;fm-codigo;subfamilia;espessura-ini;…',
    validate: 'Validar',
    validating: 'Validando…',
    readLines: (n: number, name: string) => `${n} linha(s) de dados lida(s) de ${name}`,
    validation: 'Validação',
    validCount: (n: number) => `${n} linha(s) validada(s)`,
    errorCount: (n: number) => `${n} com erro`,
    apply: 'Aplicar linhas validadas',
    result: (a: number, i: number, u: number) =>
      `Aplicado: ${a} linha(s) — ${i} incluída(s), ${u} atualizada(s).`,
    status: { pending: 'Pendente', valid: 'Validado', error: 'Erro', applied: 'Aplicado' },
    history: 'Histórico de importações',
    noHistory: 'Nenhuma importação ainda.',
    cols: {
      line: 'Linha',
      content: 'Conteúdo',
      status: 'Situação',
      message: 'Mensagem',
      number: 'Nº',
      file: 'Arquivo',
      user: 'Usuário',
      when: 'Data',
      valid: 'Válidas',
      errors: 'Erros',
    },
  },
  cadastros: {
    title: 'Cadastros',
    tabs: {
      machines: 'Máquinas',
      shifts: 'Turnos',
      capacity: 'Capacidade',
      motherWidths: 'Larguras mãe',
      changeoverTypes: 'Tipos de troca',
      setup: 'Matriz de troca',
      calendar: 'Calendário',
    },
    readOnlyHint: 'Somente consulta: só os perfis Planejador e Administrador alteram cadastros.',
    empty: 'Nenhum registro.',
    actions: 'Ações',
    save: 'Salvar',
    undo: 'Desfazer',
    delete: 'Excluir',
    confirmDelete: 'Confirmar exclusão',
    cancel: 'Cancelar',
    saved: 'Salvo',
    reload: 'Recarregar',
    add: 'Adicionar',
    conflictTitle: 'Alterado por outra pessoa.',
    conflictText: 'Recarregue para ver a versão atual; as suas alterações desta linha não foram gravadas.',
    all: 'Todas',
    yes: 'Sim',
    no: 'Não',
    loadError: 'Não foi possível carregar o cadastro.',
    machines: {
      hint: 'Cadastro fixo: edite turnos, OEE planejado, limites de largura e se a máquina está ativa. Não há criar nem excluir.',
      machine: 'Máquina',
      description: 'Descrição',
      group: 'Grupo',
      line: 'Linha',
      shifts: 'Turnos/dia',
      oee: 'OEE planejado (%)',
      minWidth: 'Largura mín. (mm)',
      maxWidth: 'Largura máx. (mm)',
      active: 'Ativa',
    },
    shifts: {
      hint: 'Turnos A, B e C (cadastro fixo). As horas de cada turno são o que entra no cálculo das horas disponíveis do calendário; o horário é só referência.',
      code: 'Turno',
      description: 'Descrição',
      start: 'Início',
      end: 'Fim',
      hours: 'Horas',
      active: 'Ativo',
    },
    capacity: {
      hint: 'Velocidade standard por máquina, família, espessura e largura. O kg/h teórico vem calculado do Datasul.',
      machine: 'Máquina',
      family: 'Família',
      subfamily: 'Subfamília',
      thickness: 'Espessura (mm)',
      width: 'Largura (mm)',
      weight: 'Peso específico',
      speed: 'm/min',
      fixedRate: 'Taxa fixa (kg/h)',
      theoretical: 'kg/h teórico',
      setup: 'Setup (min)',
      loss: 'Perda (%)',
      priority: 'Prioridade',
      active: 'Ativa',
      any: 'qualquer',
      newTitle: 'Nova faixa de capacidade',
    },
    motherWidths: {
      hint: 'Larguras mãe aceitas por Slitter. A largura padrão é a usada quando o pedido não especifica.',
      machine: 'Slitter',
      width: 'Largura mãe (mm)',
      default: 'Padrão',
      trim: 'Refile/lado (mm)',
      lanes: 'Máx. pistas',
      active: 'Ativa',
      newTitle: 'Nova largura mãe',
    },
    types: {
      hint: 'Atributos que geram setup entre sequências. O modo define como a troca é comparada.',
      code: 'Tipo',
      description: 'Descrição',
      source: 'Origem do valor',
      mode: 'Modo',
      mode1: 'De/para',
      mode2: 'Qualquer mudança',
      mode3: 'Faixa numérica',
      tolerance: 'Tolerância',
      accumulates: 'Acumula',
      active: 'Ativo',
      newTitle: 'Novo tipo de troca',
    },
    matrix: {
      hint: 'Tempo e perda por troca. "*" em máquina vale para todas as máquinas; "*" em De/Para vale para qualquer valor. Sequência não permitida bloqueia a programação.',
      machine: 'Máquina',
      type: 'Tipo',
      from: 'De',
      to: 'Para',
      minutes: 'Setup (min)',
      lossKg: 'Perda (kg)',
      allowed: 'Permitido',
      note: 'Observação',
      group: 'Grupo',
      newTitle: 'Nova regra de troca',
    },
    calendar: {
      hint: 'Dias gerados por máquina. Clique num dia para ver e corrigir (parada planejada, observação, turnos).',
      month: 'Mês',
      machine: 'Máquina',
      generate: 'Gerar calendário',
      generateTitle: 'Gerar dias do calendário',
      generateHint:
        'Cria os dias que ainda não existem no intervalo. Dia que já existe não é sobrescrito (parada e observação editadas ficam).',
      from: 'De',
      to: 'Até',
      machines: 'Máquinas',
      run: 'Gerar',
      generated: (g: number, e: number) => `${g} dia(s) gerado(s), ${e} já existente(s)`,
      day: 'Dia',
      shifts: 'Turnos',
      availableHours: 'Horas disponíveis',
      stopHours: 'Horas de parada',
      stopReason: 'Motivo da parada',
      note: 'Observação',
      notGenerated: 'Dia não gerado. Use "Gerar calendário".',
      bulkTitle: 'Aplicar parada a várias máquinas',
      bulkHint: 'Aplica a mesma parada ao dia escolhido nas máquinas marcadas (apenas dias já gerados).',
      date: 'Data',
      hours: 'Horas de parada a aplicar',
      reason: 'Motivo',
      apply: 'Aplicar parada',
      applied: (ok: number, err: number) => `${ok} aplicada(s)${err ? `, ${err} com erro` : ''}`,
      legendStop: 'com parada',
      legendNormal: 'normal',
      legendMissing: 'não gerado',
    },
  },
  password: {
    title: 'Senha',
    description:
      'Troque a senha de acesso. Ao trocar, as suas outras sessões são encerradas e os dispositivos confiáveis são esquecidos.',
    current: 'Senha atual',
    next: 'Nova senha',
    confirm: 'Repita a nova senha',
    rulesTitle: 'A nova senha precisa ter:',
    differentFromCurrent: 'Diferente da senha atual',
    mismatch: 'As senhas não conferem.',
    submit: 'Trocar senha',
    submitting: 'Trocando…',
    done: 'Senha alterada. Suas outras sessões foram encerradas.',
    show: 'Mostrar senhas',
    forcedTitle: 'Defina uma nova senha',
    forcedText:
      'Sua senha atual é provisória (definida pelo administrador ou gerada pelo sistema). Escolha uma senha só sua para continuar.',
    continue: 'Continuar',
  },
  account: {
    link: 'Segurança da conta',
    title: 'Segurança da conta',
    required:
      'O administrador exige verificação em duas etapas para o seu perfil. Ative abaixo para continuar usando o sistema.',
    twoFactor: {
      title: 'Verificação em duas etapas (2FA)',
      description:
        'Além da senha, o login pede um código de 6 dígitos gerado por um aplicativo no seu celular (Microsoft Authenticator, Google Authenticator, Authy…).',
      statusOn: 'Ativada',
      statusOff: 'Desativada',
      since: (when: string) => `desde ${when}`,
      requiredByPolicy: 'Exigida pelo administrador para o seu perfil.',
      enable: 'Ativar',
      disable: 'Desativar',
      step1: '1. Leia o QR code no aplicativo autenticador',
      step1Manual: 'Ou digite este segredo no aplicativo:',
      step2: '2. Digite o código de 6 dígitos que o aplicativo mostra',
      confirm: 'Confirmar e ativar',
      cancel: 'Cancelar',
      recoveryTitle: 'Guarde seus códigos de recuperação',
      recoveryText:
        'Cada código vale uma única vez e substitui o aplicativo se você perder o celular. Eles não serão mostrados de novo.',
      recoverySaved: 'Guardei os códigos em local seguro',
      copy: 'Copiar códigos',
      copied: 'Copiados',
      done: 'Concluir',
      codesLeft: (n: number) => `${n} código(s) de recuperação restante(s)`,
      codesLow: 'Restam poucos códigos. Gere novos.',
      newCodes: 'Gerar novos códigos',
      devices: (n: number) => `${n} dispositivo(s) confiável(is)`,
      revokeDevices: 'Esquecer dispositivos confiáveis',
      password: 'Sua senha',
      currentCode: 'Código do aplicativo ou de recuperação',
      disableTitle: 'Desativar a verificação em duas etapas',
      regenerateTitle: 'Gerar novos códigos de recuperação',
      regenerateText: 'Os códigos antigos deixam de valer.',
      working: 'Aguarde…',
    },
  },
  admin: {
    title: 'Administração',
    nav: 'Administração',
    security: {
      title: 'Política de verificação em duas etapas',
      description:
        'Por padrão, todos os perfis são obrigados a usar 2FA. Desmarque o perfil para torná-lo opcional; quem é obrigado e ainda não ativou só consegue abrir a tela de segurança até concluir.',
      save: 'Salvar política',
      saved: 'Política salva.',
    },
    users: {
      title: 'Usuários',
      newTitle: 'Novo usuário',
      newHint:
        'A senha informada é provisória: a pessoa será obrigada a trocá-la no primeiro acesso. Envie a senha por um canal seguro.',
      email: 'E-mail',
      machine: 'Máquina',
      profile: 'Perfil',
      tempPassword: 'Senha provisória',
      generate: 'Gerar',
      create: 'Criar usuário',
      created: (nome: string) => `Usuário ${nome} criado. Senha provisória:`,
      resetPassword: 'Redefinir senha',
      resetPasswordTitle: (nome: string) => `Nova senha provisória para ${nome}`,
      resetPasswordHint:
        'A pessoa será desconectada de todos os lugares e obrigada a trocar a senha no próximo acesso.',
      apply: 'Aplicar',
      cancel: 'Cancelar',
      passwordSet: (nome: string) => `Senha de ${nome} redefinida. Senha provisória:`,
      copy: 'Copiar',
      copied: 'Copiada',
      shownOnce: 'Anote agora: ela não será exibida de novo.',
      you: '(você)',
      login: 'E-mail',
      name: 'Nome',
      role: 'Perfil',
      twoFactor: '2FA',
      lastLogin: 'Último acesso',
      reset: 'Redefinir 2FA',
      resetConfirm: (name: string) =>
        `Redefinir o 2FA de ${name}? A pessoa será desconectada e poderá entrar só com a senha.`,
      yes: 'Sim, redefinir',
      no: 'Cancelar',
      never: 'nunca',
    },
  },
  shipments: {
    title: 'Importação em Andamento',
    hint: 'Embarques de mercadoria importada: o PCP usa esta tela para saber o que está em trânsito e quando chega.',
    tabs: { progress: 'Em andamento', history: 'Histórico' },
    newShipment: 'Novo embarque',
    export: 'Exportar',
    exportXlsx: 'Excel (.xlsx)',
    exportCsv: 'CSV',
    initialLoad: 'Carga inicial',
    lateCounter: (n: number) => (n === 1 ? '1 embarque atrasado' : `${n} embarques atrasados`),
    noLate: 'Nenhum embarque atrasado',
    onlyLate: 'Só atrasados',
    filters: { item: 'Item', nr: 'Processo', from: 'Entrega de', to: 'Entrega até', status: 'Situação' },
    allStatuses: 'Todas',
    empty: 'Nenhum embarque encontrado com esses filtros.',
    columns: {
      nr: 'Processo',
      status: 'Situação',
      exporter: 'Exportador',
      items: 'Itens',
      eta: 'ETA prev.',
      delivery: 'Entrega prev.',
      late: 'Atraso',
    },
    late: 'Atrasado',
    noDate: 'sem data',
    status: {
      1: 'Order',
      2: 'In transit',
      3: 'Clearance',
      4: 'In house',
      5: 'Port',
      6: 'Canceled',
      7: 'Entreposto',
    } as Record<number, string>,
    modal: { MARITIMO: 'Marítimo', AEREO: 'Aéreo', RODOVIARIO: 'Rodoviário' } as Record<string, string>,
    readOnly: 'Somente consulta: só os perfis Importação e Administrador alteram embarques.',
    form: {
      titleNew: 'Novo embarque',
      title: (nr: string) => `Embarque ${nr}`,
      back: 'Voltar à lista',
      header: 'Cabeçalho',
      items: 'Itens',
      invoices: 'Faturas (financeiro)',
      nr: 'Processo (NNN/AA)',
      status: 'Situação',
      exporter: 'Exportador',
      incoterm: 'Incoterm',
      origin: 'Origem (país)',
      modal: 'Modal',
      broker: 'Despachante',
      terminal: 'Terminal',
      blAwb: 'Booking / BL / AWB',
      dtBl: 'Data do BL/AWB',
      etdPrev: 'ETD previsto',
      etdReal: 'ETD real',
      etaPrev: 'ETA previsto',
      etaReal: 'ETA real',
      desembPrev: 'Desembaraço previsto',
      desembReal: 'Desembaraço real',
      entregaPrev: 'Entrega prevista',
      entregaReal: 'Entrega real',
      nf: 'NF de entrada',
      dtNf: 'Data da NF',
      drawback: 'Drawback',
      storageDue: 'Venc. armazenagem',
      obs: 'Observações',
      item: 'Item',
      order: 'Ordem de compra',
      chooseOrder: 'Escolha a ordem…',
      noOrders: 'Sem ordens abertas para o item',
      qty: 'Quantidade',
      unit: 'UN',
      price: 'Preço',
      noOrderMigrated: 'sem ordem (migrado)',
      addItem: 'Adicionar item',
      removeItem: 'Remover',
      invoice: 'Invoice',
      invoiceDate: 'Data',
      amount: 'Valor',
      currency: 'Moeda',
      amountBrl: 'Valor BRL',
      due: 'Vencimento',
      payment: 'Cond. pagto',
      clearanceCost: 'Custo desemb. BRL',
      addInvoice: 'Adicionar fatura',
      save: 'Gravar',
      saving: 'Gravando…',
      saved: 'Embarque gravado.',
      confirm: 'Confirmar e gravar',
      warnings: 'Avisos',
      errors: 'Corrija antes de gravar',
      conflict: 'Este embarque foi alterado por outra pessoa. Recarregue para ver a versão atual.',
      reload: 'Recarregar',
    },
    load: {
      title: 'Carga inicial da planilha',
      hint: 'Envie a aba IMPORTAÇÃO 2026 (.xlsx). Primeiro simule: nada é gravado até você confirmar.',
      file: 'Arquivo',
      simulate: 'Simular',
      run: 'Importar de verdade',
      read: 'linhas lidas',
      shipments: 'embarques',
      imported: 'linhas importadas',
      rejected: 'linhas rejeitadas',
      noOrder: 'sem ordem vinculada',
      simulation: 'Simulação: nada foi gravado.',
      done: 'Carga concluída.',
      reasons: {
        ITEM_INEXISTENTE: 'Item inexistente no Datasul',
        UNIDADE_DIVERGENTE: 'Unidade divergente',
        PROCESSO_DUPLICADO: 'Processo duplicado',
        DATA_FUTURA_EM_CAMPO_REAL: 'Data futura em campo real',
        SITUACAO_INVALIDA: 'Situação inválida',
        DADO_OBRIGATORIO: 'Dado obrigatório ausente',
      } as Record<string, string>,
      line: 'Linha',
      reason: 'Motivo',
      message: 'Mensagem',
    },
    auditTitle: 'Auditoria',
    auditHint: 'Quem alterou o quê, campo a campo. Exportações também ficam registradas.',
    auditColumns: {
      when: 'Quando',
      user: 'Usuário',
      profile: 'Perfil',
      resource: 'Recurso',
      key: 'Chave',
      action: 'Ação',
      field: 'Campo',
      before: 'Antes',
      after: 'Depois',
    },
  },
  roles: {
    planner: 'Planejador PCP',
    viewer: 'Consulta',
    operator: 'Operador',
    admin: 'Administrador',
    importacao: 'Importação',
    financeiro: 'Financeiro',
  },
  plan: {
    selectorLabel: 'Plano',
    summaryLabel: 'Resumo do plano',
    none: 'Nenhum plano disponível',
    loading: 'Carregando planos…',
    status: { draft: 'Rascunho', published: 'Publicado', superseded: 'Substituído' },
    publishedBanner: (when: string, by: string) =>
      `Plano publicado em ${when} por ${by} — para editar, crie uma cópia.`,
    supersededBanner: 'Plano substituído por um plano publicado mais novo — somente leitura.',
    kpi: {
      orders: 'Pedidos na carteira',
      onTime: 'No prazo',
      atRisk: 'Em risco',
      late: 'Atrasados',
      unscheduled: 'Sem máquina',
      bottleneck: 'Gargalo da semana',
      totalKg: 'kg a produzir',
    },
  },
  orderStatus: {
    onTime: 'No prazo',
    atRisk: 'Em risco',
    late: 'Atrasado',
    unscheduled: 'Sem máquina',
  },
  stepStatus: {
    planned: 'Programada',
    inProgress: 'Em produção',
    curing: 'Em cura',
    done: 'Concluída',
    cancelled: 'Cancelada',
  },
  states: {
    loading: 'Carregando…',
    retry: 'Tentar de novo',
    errorTitle: 'Não foi possível carregar',
    datasulDown: 'O Datasul não respondeu. Tente de novo em instantes.',
    forbiddenTitle: 'Sem acesso',
    forbidden: 'Seu perfil não tem acesso a esta tela.',
    notFoundTitle: 'Página não encontrada',
    notFound: 'O endereço não existe neste módulo.',
    backHome: 'Voltar ao início',
  },
  health: {
    label: 'Datasul',
    ok: 'conectado',
    mock: 'mock (dados simulados)',
    down: 'fora do ar',
    unknown: 'verificando…',
    bff: 'BFF',
  },
  theme: {
    toggle: 'Alternar tema',
    prefix: 'Tema',
    light: 'Claro',
    dark: 'Escuro',
    system: 'Automático',
  },
  placeholder: {
    milestone: (m: string) => `Esta tela entra no marco ${m}.`,
    wiredHint: 'A estrutura, o plano selecionado e a conexão com o BFF já estão prontos.',
    load: { title: 'Carga × Capacidade', milestone: 'M2' },
    schedule: { title: 'Programação diária', milestone: 'M3' },
    orders: { title: 'Carteira e prioridade', milestone: 'M2' },
    materials: { title: 'Matéria-prima', milestone: 'M4' },
    master: { title: 'Cadastros', milestone: 'M1' },
    import: { title: 'Importar Excel', milestone: 'M1' },
    plans: { title: 'Planos', milestone: 'M2' },
  },
  operator: {
    title: 'Programação do dia',
    machine: 'Máquina',
    empty: 'Nenhuma etapa programada.',
    refreshEvery: 'Atualiza sozinho a cada 5 minutos',
    noMachine: 'Seu usuário não está ligado a uma máquina. Fale com a TI.',
    noPublished: 'Ainda não há plano publicado.',
    cols: {
      sequence: 'Seq',
      order: 'Pedido',
      customer: 'Cliente',
      item: 'Item',
      kg: 'kg',
      thickness: 'Espessura (mm)',
      width: 'Largura (mm)',
      cut: 'Corte',
      setup: 'Setup',
      start: 'Início',
      end: 'Fim',
      status: 'Situação',
    },
  },
} as const;

export type Messages = typeof ptBR;
export const t = ptBR;
